/**
 * Lien date display helpers: prefer recording dates, fall back to document/creation dates.
 */

function parseComparable(value) {
    if (value == null || value === '' || String(value).startsWith('0000-00-00')) {
        return null
    }
    const t = new Date(value).getTime()
    return Number.isFinite(t) ? t : null
}

function pickLatestDate(...values) {
    let best = null
    let bestTs = null
    for (const value of values) {
        const ts = parseComparable(value)
        if (ts == null) {
            continue
        }
        if (bestTs == null || ts > bestTs) {
            bestTs = ts
            best = value
        }
    }
    return best
}

/**
 * Row-level display date: latest of practice/patient recorded_at, else display_date/date.
 */
export function getLienDisplayDate(lien) {
    if (!lien) {
        return null
    }
    if (lien.display_date) {
        return lien.display_date
    }
    const recorded = pickLatestDate(lien.practice_recorded_at, lien.patient_recorded_at)
    if (recorded) {
        return recorded
    }
    return lien.practice_date || lien.patient_date || lien.date || null
}

/**
 * Creation/document date for "Created on …" hover.
 */
export function getLienCreatedDate(lien) {
    if (!lien) {
        return null
    }
    if (lien.created_date) {
        return lien.created_date
    }
    return lien.practice_date || lien.patient_date || lien.date || null
}

/**
 * County table: prefer that side's recording date, else side document date, else main date.
 */
export function getLienDisplayDateForSide(lien, side) {
    if (!lien) {
        return null
    }
    const normalized = String(side || '').toLowerCase()
    if (normalized === 'practice') {
        return lien.practice_recorded_at || lien.practice_date || lien.date || null
    }
    if (normalized === 'patient') {
        return lien.patient_recorded_at || lien.patient_date || lien.date || null
    }
    return getLienDisplayDate(lien)
}

export function getLienCreatedDateForSide(lien, side) {
    if (!lien) {
        return null
    }
    const normalized = String(side || '').toLowerCase()
    if (normalized === 'practice') {
        return lien.practice_date || lien.date || null
    }
    if (normalized === 'patient') {
        return lien.patient_date || lien.date || null
    }
    return getLienCreatedDate(lien)
}

/**
 * True when the visible date is a recording date that differs from the created/document date
 * (so hover should show Created on …).
 */
export function lienDisplayUsesRecordingDate(lien, side = null) {
    if (!lien) {
        return false
    }
    const display = side ? getLienDisplayDateForSide(lien, side) : getLienDisplayDate(lien)
    const created = side ? getLienCreatedDateForSide(lien, side) : getLienCreatedDate(lien)
    const recorded = side
        ? (String(side).toLowerCase() === 'practice' ? lien.practice_recorded_at : lien.patient_recorded_at)
        : pickLatestDate(lien.practice_recorded_at, lien.patient_recorded_at)

    if (!recorded || !display || !created) {
        return false
    }

    const displayDay = String(display).slice(0, 10)
    const createdDay = String(created).slice(0, 10)
    return displayDay !== createdDay
}

export function formatCreatedOnHover(createdDate, formatDateLocal) {
    if (!createdDate || typeof formatDateLocal !== 'function') {
        return null
    }
    const formatted = formatDateLocal(createdDate)
    if (!formatted) {
        return null
    }
    return `Created on ${formatted}`
}

/** WorkQ row: created-date field paired with a display label. */
export const WORKQ_LIEN_CREATED_FIELD_BY_DISPLAY = {
    notice_date: 'notice_created_date',
    lien_date: 'lien_created_date',
    amended_date: 'amended_created_date',
    satisfaction_date: 'satisfaction_created_date',
}

export function getWorkQLienCreatedDate(item, displayField) {
    if (!item || !displayField) {
        return null
    }
    const createdField = WORKQ_LIEN_CREATED_FIELD_BY_DISPLAY[displayField]
    if (createdField && item[createdField]) {
        return item[createdField]
    }
    return null
}
